import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useBranches } from "@/hooks/useBranches";
import { useCashClosings, useCashRegister } from "@/hooks/useCash";
import { logAudit } from "@/lib/audit";
import { formatDateTime, formatMoney } from "@/lib/format";
import MovementModal from "@/pages/admin/cash/components/MovementModal";
import CloseRegisterModal from "@/pages/admin/cash/components/CloseRegisterModal";

export default function AdminCashPage() {
  const { profile, isAdmin, hasPermission } = useAuth();
  const toast = useToast();
  const { branches } = useBranches();

  const activeBranches = useMemo(() => branches.filter((b) => b.active), [branches]);
  const [branchId, setBranchId] = useState<string | null>(profile?.branch_id ?? null);

  useEffect(() => {
    if (profile?.branch_id) setBranchId(profile.branch_id);
  }, [profile?.branch_id]);

  useEffect(() => {
    if (!branchId && activeBranches.length > 0) setBranchId(activeBranches[0].id);
  }, [branchId, activeBranches]);

  const cash = useCashRegister(profile?.id ?? null, branchId);
  const closingsData = useCashClosings();

  const [openingAmount, setOpeningAmount] = useState("");
  const [opening, setOpening] = useState(false);
  const [movementOpen, setMovementOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const branchName = activeBranches.find((b) => b.id === branchId)?.name ?? "—";
  const userName = `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario";

  const canView = isAdmin || hasPermission("cash.view") || hasPermission("pos.view");
  const canClose = isAdmin || hasPermission("cash.close") || hasPermission("pos.view");

  const handleOpenRegister = async () => {
    const amount = Number(openingAmount) || 0;
    setOpening(true);
    try {
      const result = await cash.open(amount);
      if (!result.ok) {
        toast.error(result.error ?? "No se pudo abrir la caja.");
        return;
      }
      await logAudit({
        userId: profile?.id ?? null,
        userName,
        action: "abrir_caja",
        entity: "cash_registers",
        entityId: cash.register?.id ?? profile?.id ?? "",
        branchId,
        newValue: { opening_amount: amount },
      });
      toast.success("Caja abierta.");
      setOpeningAmount("");
    } finally {
      setOpening(false);
    }
  };

  const handleMovement = async (type: "ingreso" | "egreso", amount: number, concept: string) => {
    setProcessing(true);
    try {
      const result = await cash.move(type, amount, concept);
      if (!result.ok) {
        toast.error(result.error ?? "No se pudo registrar el movimiento.");
        return;
      }
      await logAudit({
        userId: profile?.id ?? null,
        userName,
        action: "movimiento_caja",
        entity: "cash_movements",
        entityId: cash.register?.id ?? "",
        branchId,
        newValue: { type, amount, concept },
      });
      toast.success("Movimiento registrado.");
      setMovementOpen(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseRegister = async (counted: number) => {
    setProcessing(true);
    try {
      const result = await cash.close(counted);
      if (!result.ok) {
        toast.error(result.error ?? "No se pudo cerrar la caja.");
        return;
      }
      await logAudit({
        userId: profile?.id ?? null,
        userName,
        action: "cerrar_caja",
        entity: "cash_closings",
        entityId: cash.register?.id ?? "",
        branchId,
        newValue: {
          expected_total: result.expected,
          counted_total: result.counted,
          difference: result.difference,
        },
      });
      toast.success(
        `Caja cerrada · ${Number(result.difference ?? 0) === 0 ? "cuadrada" : "con diferencia"}`,
      );
      setCloseOpen(false);
      closingsData.reload();
    } finally {
      setProcessing(false);
    }
  };

  const refresh = useCallback(() => {
    cash.reload();
    closingsData.reload();
  }, [cash, closingsData]);

  const summary = cash.summary;
  const diffTotal = useMemo(
    () => closingsData.closings.reduce((sum, c) => sum + Number(c.difference ?? 0), 0),
    [closingsData.closings],
  );

  const statCards = [
    {
      key: "status",
      label: "Estado de caja",
      value: cash.register ? "Abierta" : "Cerrada",
      hint: cash.register ? `Desde ${formatDateTime(cash.register.opened_at)}` : "Sin turno abierto",
      icon: cash.register ? "ri-lock-unlock-line" : "ri-lock-line",
      tone: cash.register ? "accent" : "secondary",
    },
    {
      key: "expected",
      label: "Efectivo esperado",
      value: formatMoney(summary?.expected_total ?? 0),
      hint: "Apertura + ventas efectivo + movimientos",
      icon: "ri-money-dollar-circle-line",
      tone: "primary",
    },
    {
      key: "cash_sales",
      label: "Ventas en efectivo",
      value: formatMoney(summary?.cash_sales ?? 0),
      hint: `${summary?.sales_count ?? 0} venta(s) del turno`,
      icon: "ri-wallet-3-line",
      tone: "secondary",
    },
    {
      key: "diff",
      label: "Diferencias históricas",
      value: formatMoney(diffTotal),
      hint: `${closingsData.closings.length} cierre(s)`,
      icon: "ri-scales-3-line",
      tone: diffTotal < 0 ? "primary" : "accent",
    },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-100 text-secondary-800",
  };

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-background-200 bg-background-100 px-4 py-16 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-lock-2-line text-2xl"></i>
        </span>
        <p className="text-sm font-semibold text-foreground-800">Acceso restringido</p>
        <p className="max-w-sm text-xs text-foreground-500">
          Tu usuario no tiene permiso para ver el cuadre de caja.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Cuadre de Caja</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Apertura, movimientos de caja y cierre por usuario y sucursal con diferencias.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {activeBranches.length > 1 ? (
            <select
              value={branchId ?? ""}
              onChange={(event) => setBranchId(event.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-semibold text-foreground-800 outline-none focus:border-primary-400"
            >
              {activeBranches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-semibold text-foreground-700">
              {branchName}
            </span>
          )}
          <button
            type="button"
            onClick={refresh}
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className="ri-refresh-line text-base"></i>
            Actualizar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
              <i className={`${card.icon} text-lg`}></i>
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground-500">{card.label}</p>
              <p className="font-heading mt-0.5 truncate text-lg font-bold text-foreground-950">{card.value}</p>
              <p className="mt-0.5 truncate text-[11px] text-foreground-400">{card.hint}</p>
            </div>
          </div>
        ))}
      </div>

      {cash.error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {cash.error}
          </span>
          <button
            type="button"
            onClick={refresh}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {cash.loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando caja...
        </div>
      ) : cash.register ? (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[380px_1fr]">
          <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-heading text-base font-semibold text-foreground-950">Turno abierto</h2>
              <span className="rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-semibold text-accent-800">
                Abierta
              </span>
            </div>
            <p className="text-xs text-foreground-500">
              {userName} · {branchName} · desde {formatDateTime(cash.register.opened_at)}
            </p>

            <dl className="flex flex-col gap-2 rounded-md border border-background-200 bg-background-100 p-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">Fondo de apertura</dt>
                <dd className="font-medium text-foreground-900">{formatMoney(summary?.opening_amount ?? 0)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">Ventas en efectivo</dt>
                <dd className="font-medium text-foreground-900">{formatMoney(summary?.cash_sales ?? 0)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">Otros pagos (tarjeta/transfer.)</dt>
                <dd className="font-medium text-foreground-900">{formatMoney(summary?.other_sales ?? 0)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">Ingresos de caja</dt>
                <dd className="font-medium text-accent-800">{formatMoney(summary?.income ?? 0)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">Egresos de caja</dt>
                <dd className="font-medium text-primary-700">-{formatMoney(summary?.expense ?? 0)}</dd>
              </div>
              <div className="mt-1 flex items-center justify-between border-t border-background-200 pt-2.5">
                <dt className="font-heading text-sm font-semibold text-foreground-950">Esperado en caja</dt>
                <dd className="font-heading text-xl font-bold text-primary-600">
                  {formatMoney(summary?.expected_total ?? 0)}
                </dd>
              </div>
            </dl>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setMovementOpen(true)}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
              >
                <i className="ri-exchange-dollar-line text-base"></i>
                Registrar movimiento
              </button>
              {canClose ? (
                <button
                  type="button"
                  onClick={() => setCloseOpen(true)}
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-lock-line text-base"></i>
                  Cerrar caja
                </button>
              ) : null}
            </div>
          </section>

          <section className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">
              Movimientos del turno
            </h2>
            <p className="mt-1 text-xs text-foreground-500">
              {cash.movements.length} movimiento(s) registrado(s).
            </p>

            {cash.movements.length === 0 ? (
              <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-background-300 bg-background-50 px-4 py-12 text-center">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-exchange-dollar-line text-xl"></i>
                </span>
                <p className="text-sm font-medium text-foreground-700">Sin movimientos</p>
                <p className="max-w-sm text-xs text-foreground-500">
                  Registra ingresos o egresos de caja (fondo adicional, retiros, pagos) para que el cuadre sea exacto.
                </p>
              </div>
            ) : (
              <ul className="rkc-scroll-thin mt-4 flex max-h-[380px] flex-col divide-y divide-background-200 overflow-y-auto">
                {cash.movements.map((mov) => {
                  const isIncome = mov.movement_type === "ingreso";
                  return (
                    <li key={mov.id} className="flex items-center gap-3 py-3">
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                          isIncome ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-700"
                        }`}
                      >
                        <i className={`${isIncome ? "ri-arrow-down-line" : "ri-arrow-up-line"} text-base`}></i>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground-900">
                          {mov.concept || (isIncome ? "Ingreso" : "Egreso")}
                        </p>
                        <p className="truncate text-[11px] text-foreground-400">
                          {mov.user_name || "Usuario"} · {formatDateTime(mov.created_at)}
                        </p>
                      </div>
                      <span
                        className={`font-heading shrink-0 text-sm font-bold ${
                          isIncome ? "text-accent-800" : "text-primary-700"
                        }`}
                      >
                        {isIncome ? "+" : "-"}
                        {formatMoney(mov.amount)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      ) : (
        <section className="flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 px-4 py-12 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-lock-unlock-line text-2xl"></i>
          </span>
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950">Abrir caja</h2>
            <p className="mx-auto mt-1.5 max-w-md text-sm text-foreground-500">
              Ingresa el fondo inicial (efectivo con el que comienzas el turno) para {userName} en {branchName}.
            </p>
          </div>
          <div className="flex w-full max-w-xs flex-col gap-2 sm:flex-row">
            <input
              value={openingAmount}
              onChange={(event) => setOpeningAmount(event.target.value.replace(/[^0-9.]/g, ""))}
              inputMode="decimal"
              placeholder="Fondo inicial RD$"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400"
            />
            <button
              type="button"
              onClick={handleOpenRegister}
              disabled={opening}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600 disabled:opacity-60"
            >
              {opening ? (
                <i className="ri-loader-4-line animate-spin text-base"></i>
              ) : (
                <i className="ri-lock-unlock-line text-base"></i>
              )}
              Abrir caja
            </button>
          </div>
        </section>
      )}

      <section className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading text-base font-semibold text-foreground-950">Historial de cierres</h2>
          <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-800">
            {closingsData.closings.length} cierre(s)
          </span>
        </div>

        {closingsData.error ? (
          <div className="mt-4 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800">
            <i className="ri-error-warning-line text-base"></i>
            {closingsData.error}
          </div>
        ) : closingsData.loading ? (
          <div className="mt-4 flex items-center justify-center gap-3 py-10 text-sm text-foreground-500">
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            Cargando cierres...
          </div>
        ) : closingsData.closings.length === 0 ? (
          <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-background-300 bg-background-50 px-4 py-12 text-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-scales-3-line text-xl"></i>
            </span>
            <p className="text-sm font-medium text-foreground-700">Sin cierres todavía</p>
            <p className="max-w-sm text-xs text-foreground-500">
              Al cerrar una caja, el cierre quedará guardado aquí con su diferencia.
            </p>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="border-b border-background-200 text-left text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="py-2.5 pr-4">Fecha de cierre</th>
                  <th className="py-2.5 pr-4">Usuario</th>
                  <th className="py-2.5 pr-4">Sucursal</th>
                  <th className="py-2.5 pr-4 text-right">Esperado</th>
                  <th className="py-2.5 pr-4 text-right">Contado</th>
                  <th className="py-2.5 text-right">Diferencia</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {closingsData.closings.map((closing) => {
                  const diff = Number(closing.difference ?? 0);
                  const diffTone =
                    Math.abs(diff) < 0.005
                      ? "text-accent-800"
                      : diff < 0
                        ? "text-primary-700"
                        : "text-secondary-900";
                  return (
                    <tr key={closing.id}>
                      <td className="py-3 pr-4 text-sm text-foreground-700">{formatDateTime(closing.closed_at)}</td>
                      <td className="py-3 pr-4 text-sm text-foreground-700">{closing.user_name ?? "—"}</td>
                      <td className="py-3 pr-4 text-sm text-foreground-700">{closing.branch_name ?? "—"}</td>
                      <td className="py-3 pr-4 text-right text-sm text-foreground-700">
                        {formatMoney(closing.expected_total)}
                      </td>
                      <td className="py-3 pr-4 text-right text-sm text-foreground-700">
                        {formatMoney(closing.counted_total)}
                      </td>
                      <td className={`py-3 text-right text-sm font-bold ${diffTone}`}>
                        {diff > 0 ? "+" : ""}
                        {formatMoney(diff)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <MovementModal
        open={movementOpen}
        onClose={() => setMovementOpen(false)}
        processing={processing}
        onSubmit={handleMovement}
      />

      <CloseRegisterModal
        open={closeOpen}
        onClose={() => setCloseOpen(false)}
        summary={summary}
        processing={processing}
        onConfirm={handleCloseRegister}
      />
    </div>
  );
}